import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../components/content/axiosConfig";
import { handleLegacySessionExpired } from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const cardRadius = { borderRadius: "14px" };

const UpdatePassword = () => {
  const history = useHistory();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFisherWideUser, setIsFisherWideUser] = useState(true);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get("api/User/GetStaff", { params: { id } });
        if (!cancelled) setIsFisherWideUser(!!data?.IsAdmin);
      } catch {
        if (!cancelled) setIsFisherWideUser(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleChange = (e) => {
    const { id: fieldId, value, type, checked, name } = e.target;
    if (type === "radio") {
      setFormData({
        ...formData,
        [name]: value === "true",
      });
    } else {
      setFormData({
        ...formData,
        [fieldId]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (formData.password !== formData.confirmPassword) {
        showErrorToast("Passwords do not match");
        setIsLoading(false);
        return;
      }
      if (formData.password.length < 8) {
        showErrorToast("Password must be at least 8 characters long");
        setIsLoading(false);
        return;
      }

      await axiosInstance.post(
        `api/User/UpdatePassword`,
        {
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        },
        {
          params: { id: id },
        }
      );

      showSuccessToast("Password updated successfully");
      setIsLoading(false);
      history.push("/staff");
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to update password";
        showErrorToast(errorMessage);
      }
      console.error("Error updating password:", error);
    }
  };

  return (
    <div className="donezo-page">
      {isLoading ? (
        <Preloader />
      ) : (
        <form onSubmit={handleSubmit} className="container-fluid px-3 px-md-4 py-3 py-md-4">
          <nav aria-label="breadcrumb" className="mb-2">
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/staff" className="text-decoration-none text-muted">
                  Team
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Change password
              </li>
            </ol>
          </nav>

          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h1 className="h3 fw-semibold text-dark mb-1">Change password</h1>
              <p className="text-muted small mb-0">
                Set a new sign-in password for this team member. They will use this with their existing email.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 ms-lg-auto">
              <Link to="/staff" className="btn btn-outline-secondary rounded-pill px-4">
                Cancel
              </Link>
              <button type="submit" className="btn btn-primary rounded-pill px-4 shadow-sm">
                <i className="fa fa-lock me-2" aria-hidden />
                Save password
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4" style={cardRadius}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-wrap gap-2">
                <Link
                  to={"/UpdateStaff/" + id}
                  className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
                >
                  <i className="fa fa-user me-1 opacity-75" aria-hidden />
                  General
                </Link>
                {!isFisherWideUser ? (
                  <Link
                    to={"/ScientistPond/" + id}
                    className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
                  >
                    <i className="fa fa-water me-1 opacity-75" aria-hidden />
                    Pond access &amp; roles
                  </Link>
                ) : null}
                <span className="badge rounded-pill px-3 py-2 bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                  <i className="fa fa-key me-1 opacity-75" aria-hidden />
                  Password
                </span>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={cardRadius}>
            <div className="card-body p-4 p-lg-5">
              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em" }}>
                New password
              </h2>
              <p className="small text-muted mb-4">
                Use at least 8 characters. Share the new password securely with the team member outside of Pondstat.
              </p>
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="password" className="form-label small fw-medium text-secondary">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    className="form-control form-control-lg border-0 bg-light"
                    style={{ borderRadius: "10px" }}
                    placeholder="New password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="confirmPassword" className="form-label small fw-medium text-secondary">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    className="form-control form-control-lg border-0 bg-light"
                    style={{ borderRadius: "10px" }}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default UpdatePassword;
